/**
 * A folha de impressão do detalhamento de um colaborador do banco de horas.
 *
 * Papel: reproduz `bhPrintEmployee` da origem na `FolhaDeImpressao` — o título
 * "Banco de Horas", a linha "Detalhamento — NOME", a caixa com o nome, a linha
 * de função, departamento e admissão e o saldo atual, a caixa "Histórico
 * mensal" com o mês, os três tempos, o saldo do mês e o acumulado, e o
 * "Exportado em" no pé.
 *
 * Como usar: as regras de montar, imprimir e desmontar são as da
 * `FolhaDeImpressao`. As cores são as da tela do colaborador: crédito em
 * verde, débito em vermelho, ajuste em latão, e saldo do mês, acumulado e saldo
 * atual pelo sinal.
 */
import { type ColaboradorBancoHoras, saldoEmMinutos } from '@concierge/shared';

import { formatarCompetencia } from '@/shared/lib/formato';
import { cn } from '@/shared/lib/utils';
import {
  comAcumulado,
  corDoSaldo,
  formatarAjuste,
  formatarHoras,
  linhaDoColaborador,
} from '../banco-horas';
import { CaixaDaFolha, FolhaDeImpressao } from './folha-de-impressao';
import {
  CLASSES_DO_CORPO_DA_FOLHA,
  CLASSES_DO_NUMERO_DA_FOLHA,
  CLASSES_DO_TH_DA_FOLHA,
  linhaDeExportacao,
} from './partes-da-folha';

/** As colunas numéricas do histórico, na ordem em que saem. */
const COLUNAS_NUMERICAS = ['Crédito', 'Débito', 'Ajuste', 'Saldo mês', 'Acumulado'] as const;

interface FolhaColaboradorBancoHorasProps {
  /** O colaborador, com os meses, no momento do clique. */
  colaborador: ColaboradorBancoHoras;
  /** O instante do clique em "🖨 Imprimir / PDF", em ISO 8601 com fuso. */
  exportadaEm: string;
  /** Chamado quando a janela de impressão fecha. */
  onConcluida: () => void;
}

/**
 * A folha do detalhamento.
 *
 * @param colaborador - O colaborador com os meses.
 * @param exportadaEm - O instante do clique.
 * @param onConcluida - Chamado uma vez, no `afterprint`.
 * @returns A folha, escondida na tela e visível só na impressão.
 */
export function FolhaColaboradorBancoHoras({
  colaborador,
  exportadaEm,
  onConcluida,
}: FolhaColaboradorBancoHorasProps) {
  const linhas = comAcumulado(colaborador.meses);
  const saldo = saldoEmMinutos(colaborador.meses);

  return (
    <FolhaDeImpressao
      titulo="Banco de Horas"
      subtitulo={`Detalhamento — ${colaborador.nomeCompleto.toUpperCase()}`}
      onConcluida={onConcluida}
    >
      <CaixaDaFolha
        className="mb-5"
        titulo={
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-serif text-[15px] font-medium text-foreground">
                {colaborador.nomeCompleto}
              </div>
              <div className="mt-1 font-mono text-[9.5px] tracking-[0.06em] text-texto-fraco uppercase">
                {linhaDoColaborador(colaborador)}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="font-mono text-[9px] tracking-[0.06em] text-texto-fraco uppercase">
                Saldo atual
              </div>
              <div className={cn('font-serif text-[19px] font-semibold', corDoSaldo(saldo))}>
                {formatarHoras(saldo)}
              </div>
            </div>
          </div>
        }
      />

      <CaixaDaFolha titulo="Histórico mensal">
        <table aria-label="Histórico mensal" className="w-full border-collapse">
          <thead>
            <tr>
              <th className={CLASSES_DO_TH_DA_FOLHA}>Período</th>
              {COLUNAS_NUMERICAS.map((rotulo) => (
                <th key={rotulo} className={cn(CLASSES_DO_TH_DA_FOLHA, 'text-right')}>
                  {rotulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={CLASSES_DO_CORPO_DA_FOLHA}>
            {linhas.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-6! text-center text-texto-fraco">
                  Nenhum registro ainda.
                </td>
              </tr>
            ) : (
              linhas.map(({ mes, saldoDoMes, acumulado }) => (
                <tr key={mes.id}>
                  <td className="font-semibold text-foreground">
                    {formatarCompetencia(mes.competencia)}
                  </td>
                  <td className={cn(CLASSES_DO_NUMERO_DA_FOLHA, 'text-sucesso')}>
                    {formatarHoras(mes.creditoMin)}
                  </td>
                  <td className={cn(CLASSES_DO_NUMERO_DA_FOLHA, 'text-perigo')}>
                    {formatarHoras(mes.debitoMin)}
                  </td>
                  <td className={cn(CLASSES_DO_NUMERO_DA_FOLHA, 'text-brass-light')}>
                    {formatarAjuste(mes.ajusteMin)}
                  </td>
                  <td className={cn(CLASSES_DO_NUMERO_DA_FOLHA, corDoSaldo(saldoDoMes))}>
                    {formatarHoras(saldoDoMes)}
                  </td>
                  <td className={cn(CLASSES_DO_NUMERO_DA_FOLHA, corDoSaldo(acumulado))}>
                    {formatarHoras(acumulado)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </CaixaDaFolha>

      <p className="mt-5 font-mono text-[10.5px] tracking-[0.08em] text-brass uppercase">
        {linhaDeExportacao(exportadaEm)}
      </p>
    </FolhaDeImpressao>
  );
}
